import React, { useMemo, useState } from "react";
import {
    Container,
    Dropdown,
    Navbar,
} from "react-bootstrap";
import { useAuth0 } from "@auth0/auth0-react";
import { Link, NavLink } from "react-router-dom";
import logo from "../assets/logo.svg";
import "./Layout.css";

type LayoutProps = {
    children: React.ReactNode;
};

type IconName =
    | "info"
    | "trips"
    | "contacts"
    | "items"
    | "login"
    | "logout"
    | "account";

type IconProps = {
    name: IconName;
    size?: number;
};

function Icon({ name, size = 18 }: IconProps) {
    const paths: Record<IconName, React.ReactNode> = {
        info: (
            <>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 10.75v5" />
                <path d="M12 7.5h.01" />
            </>
        ),

        trips: (
            <>
                <path d="M8 7V5.75A1.75 1.75 0 0 1 9.75 4h4.5A1.75 1.75 0 0 1 16 5.75V7" />
                <rect x="3" y="7" width="18" height="12" rx="2" />
                <path d="M3 11.5h18" />
                <path d="M9 11.5v2" />
                <path d="M15 11.5v2" />
            </>
        ),

        contacts: (
            <>
                <circle cx="9" cy="8" r="3" />
                <path d="M3.5 19c.5-3.3 2.4-5 5.5-5s5 1.7 5.5 5" />
                <circle cx="17" cy="9" r="2.25" />
                <path d="M15.5 14.5c2.9-.4 4.6 1.1 5 4.5" />
            </>
        ),
        items: (
            <>
                <rect x="4" y="5" width="16" height="14" rx="2" />
                <path d="M8 9h8" />
                <path d="M8 13h5" />
            </>
        ),
        login: (
            <>
                <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
                <path d="M10 8l4 4-4 4" />
                <path d="M14 12H4" />
            </>
        ),

        logout: (
            <>
                <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" />
                <path d="M14 8l4 4-4 4" />
                <path d="M18 12H8" />
            </>
        ),

        account: (
            <>
                <circle cx="12" cy="8" r="3.25" />
                <path d="M5.5 20c.5-4 2.7-6 6.5-6s6 2 6.5 6" />
            </>
        ),
    };

    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
        >
            {paths[name]}
        </svg>
    );
}

function getInitials(name?: string, email?: string): string {
    const source = name?.trim() || email?.trim() || "User";

    const words = source
        .split(/[\s@._-]+/)
        .filter(Boolean)
        .slice(0, 2);

    return words
        .map((word) => word.charAt(0).toUpperCase())
        .join("");
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const {
        loginWithRedirect,
        logout,
        isAuthenticated,
        user,
    } = useAuth0();

    const [expanded, setExpanded] = useState(false);

    const displayName =
        user?.given_name ||
        user?.name ||
        user?.email?.split("@")[0] ||
        "Account";

    const initials = useMemo(
        () => getInitials(user?.name, user?.email),
        [user?.name, user?.email]
    );

    const closeNavigation = () => {
        setExpanded(false);
    };

    return (
        <>
            <Navbar
                expand="lg"
                sticky="top"
                expanded={expanded}
                onToggle={(nextExpanded) => setExpanded(nextExpanded)}
                className="ts-navbar"
            >
                <Container className="ts-navbar-container">
                    <Navbar.Brand
                        as={Link}
                        to="/"
                        className="ts-brand"
                        onClick={closeNavigation}
                    >
                        <img
                            src={logo}
                            alt="TripSplitter"
                            className="ts-logo"
                        />
                    </Navbar.Brand>

                    <Navbar.Toggle
                        aria-controls="trip-splitter-navigation"
                        className="ts-navbar-toggle"
                    >
                        <span className="ts-menu-line" />
                        <span className="ts-menu-line" />
                        <span className="ts-menu-line" />
                    </Navbar.Toggle>

                    <Navbar.Collapse id="trip-splitter-navigation">
                        <nav
                            className="ts-navigation"
                            aria-label="Main navigation"
                        >
                            <NavLink
                                to="/about"
                                className={({ isActive }) =>
                                    `ts-nav-button${isActive ? " active" : ""}`
                                }
                                onClick={closeNavigation}
                            >
                                <span className="ts-nav-icon">
                                    <Icon name="info" />
                                </span>
                                <span>How it works</span>
                            </NavLink>

                            {isAuthenticated && (
                                <NavLink
                                    to="/"
                                    end
                                    className={({ isActive }) =>
                                        `ts-nav-button${isActive ? " active" : ""}`
                                    }
                                    onClick={closeNavigation}
                                >
                                    <span className="ts-nav-icon">
                                        <Icon name="trips" />
                                    </span>
                                    <span>My trips</span>
                                </NavLink>
                            )}

                            {isAuthenticated && (
                                <NavLink
                                    to="/contacts"
                                    className={({ isActive }) =>
                                        `ts-nav-button${isActive ? " active" : ""}`
                                    }
                                    onClick={closeNavigation}
                                >
                                    <span className="ts-nav-icon">
                                        <Icon name="contacts" />
                                    </span>
                                    <span>Contacts</span>
                                </NavLink>
                            )}
                             {isAuthenticated && (
                             <NavLink
                                 to="/items"
                                 className={({ isActive }) =>
                                     `ts-nav-button${isActive ? " active" : ""}`
                                 }
                                 onClick={closeNavigation}
                             >
                                 <span className="ts-nav-icon">
                                     <Icon name="items" />
                                 </span>
                                 <span>Frequently used expenses</span>
                             </NavLink>
                         )}
                        </nav>

                        <div className="ts-account-area">
                            {!isAuthenticated ? (
                                <button
                                    type="button"
                                    className="ts-login-button"
                                    onClick={() => {
                                        closeNavigation();
                                        void loginWithRedirect();
                                    }}
                                >
                                    <Icon name="login" />
                                    <span>Log in</span>
                                </button>
                            ) : (
                                <Dropdown align="end" className="ts-user-menu">
                                    <Dropdown.Toggle
                                        id="trip-splitter-account-menu"
                                        className="ts-user-toggle"
                                    >
                                        <span className="ts-user-avatar">
                                            {user?.picture ? (
                                                <img
                                                    src={user.picture}
                                                    alt=""
                                                    referrerPolicy="no-referrer"
                                                />
                                            ) : (
                                                initials
                                            )}
                                        </span>

                                        <span className="ts-user-copy">
                                            <span className="ts-user-greeting">
                                                Signed in as
                                            </span>
                                            <span className="ts-user-name">
                                                {displayName}
                                            </span>
                                        </span>

                                        <span className="ts-user-chevron">
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    d="m7 10 5 5 5-5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu className="ts-user-dropdown">
                                        <div className="ts-dropdown-profile">
                                            <span className="ts-dropdown-avatar">
                                                {user?.picture ? (
                                                    <img
                                                        src={user.picture}
                                                        alt=""
                                                        referrerPolicy="no-referrer"
                                                    />
                                                ) : (
                                                    initials
                                                )}
                                            </span>

                                            <div className="ts-dropdown-profile-copy">
                                                <strong>{user?.name || displayName}</strong>
                                                {user?.email && <small>{user.email}</small>}
                                            </div>
                                        </div>

                                        <Dropdown.Divider />

                                        <Dropdown.Item
                                            as={Link}
                                            to="/contacts"
                                            onClick={closeNavigation}
                                            className="ts-dropdown-item"
                                        >
                                            <Icon name="account" />
                                            <span>Manage contacts</span>
                                        </Dropdown.Item>
                                         <Dropdown.Item
                                             as={Link}
                                             to="/items"
                                             onClick={closeNavigation}
                                             className="ts-dropdown-item"
                                         >
                                             <Icon name="items" />
                                             <span>Frequently used expenses</span>
                                         </Dropdown.Item>
                                        <Dropdown.Item
                                            onClick={() =>
                                                logout({
                                                    logoutParams: {
                                                        returnTo: window.location.origin,
                                                    },
                                                })
                                            }
                                            className="ts-dropdown-item ts-logout-item"
                                        >
                                            <Icon name="logout" />
                                            <span>Log out</span>
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}
                        </div>
                    </Navbar.Collapse>
                </Container>
            </Navbar>

            <main className="ts-page">
                <Container>{children}</Container>
            </main>
        </>
    );
};

export default Layout;